<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { cloneDeep, findIndex, startsWith, toString, trim, trimStart } from 'lodash-es';

import HawkTable from '~/common/components/organisms/hawk-table/hawk-table.vue';
import { convertTableUnitsData } from '~/common/utils/common.utils.js';
import { useDashboardStore } from '~/dashboard/store/dashboard.store.js';

const props = defineProps({
  data: {
    type: Object,
  },
  id: {
    type: String,
  },
  content_height: {
    type: Number,
  },
  response_data: {
    type: Object,
  },
  payload: {
    type: Object,
  },
  graph_config: {},
});
const $services = inject('$services');
const loading = ref(false);
const activities = ref([]);
const columns = ref([]);
const children = ref({});
const dashboard_store = useDashboardStore();
const activity_key_map = ref(null);
const no_data = ref(false);
const prevent_watcher = ref(false);
const print_dataset = ref([]);

const widget_data = computed(() => props.data?.data || {});

const column_key_map = {
  actual: '% Actual',
  actual_units: 'Actual',
  lag: '% Lag',
  lag_units: 'Lag',
  planned: '% Planned',
  planned_units: 'Planned',
  remaining: '% Remaining',
  remaining_units: 'Remaining',
  total_units: 'Total (Scope)',
  actual_cost: 'Actual cost',
  planned_cost: 'Planned cost',
  earned_value: 'Earned value',
  schedule_variance: 'Schedule variance',
  cost_variance: 'Cost variance',
  remaining_cost: 'Remaining cost',
  resource: 'Resource',
  priority: 'Priority',
  category: 'Category',
  tags: 'Tags',

};

async function getReports() {
  loading.value = true;

  activity_key_map.value = props.payload?.properties.activity_table_activities;
  try {
    const data = props.response_data;
    if (data && Object.keys(data)) {
      no_data.value = false;
      activities.value = generateActivities(data.data);
      print_dataset.value = [...activities.value];

      generateColumns(widget_data.value.columns, Object.values(data.data)[0]);
    }
    else {
      no_data.value = true;
      activities.value = [];
      print_dataset.value = [];
      columns.value = [];
      loading.value = false;
    }
    loading.value = false;
  }
  catch {
    loading.value = false;
  }
}
function set_absolute_remaining_duration_value(value, nestedValue) {
  if (value.status === 'completed')
    return '-';
  else if (startsWith(nestedValue, '-'))
    return `${trim(nestedValue, '-')} days overdue`;
  else return `${nestedValue} days remaining`;
}
const field_names = ['uid', 'id', 'activity_id', 'type', 'activity', 'actual_duration', 'actual_progress', 'critical', 'lag', 'lag_days', 'planned_duration', 'planned_progress', 'remaining_duration', 'remaining_progress', 'status', 'bl_start', 'bl_finish', 'planned_start', 'planned_finish', 'actual_start', 'actual_finish', 'duration', 'free_slack', 'total_slack', 'overdue_days', 'name', 'absolute_remaining_duration', 'absolute_work_rate', 'planned_work_rate', 'actual_work_rate', 'bl_work_rate', 'remaining_work_rate', 'actual_cost', 'planned_cost', 'earned_value', 'schedule_variance', 'cost_variance', 'remaining_cost', 'resources', 'priority', 'category', 'tags'];

const priority_values = {
  1: 'Critical',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Not set',
};

function generateActivities(data) {
  const array = [];
  for (const [key, value] of Object.entries(data)) {
    const current_item = {};
    if (['PROJECT', 'WBS'].includes(value.type))
      current_item.subRows = [''];

    current_item.activity_id = key;

    for (const [nestedKey, nestedValue] of Object.entries(value)) {
      if (nestedKey && !field_names.includes(nestedKey)) {
        Object.keys(nestedValue.values).forEach((valueKey) => {
          if (['lag', 'actual', 'remaining'].includes(valueKey)) {
            current_item[`${valueKey}-${nestedKey}`] = `${nestedValue.values[valueKey]}%`;
          }
          else {
            current_item[`${valueKey}-${nestedKey}`] = convertTableUnitsData(nestedValue.values[valueKey], nestedValue.units);
          }
        });
      }
      else if (nestedKey === 'name') {
        current_item.name = activity_key_map.value?.find(e => e.uid === key)?.name || nestedValue;
      }
      else if (nestedKey === 'id') {
        current_item.id = nestedValue;
      }
      else if (nestedKey === 'absolute_remaining_duration') {
        current_item[nestedKey] = set_absolute_remaining_duration_value(value, nestedValue);
      }
      else if (['resources'].includes(nestedKey)) {
        let computed_value;
        if (!nestedValue || !nestedValue.length) {
          computed_value = '';
        }
        else if (nestedValue?.[0]?.type === 'member') {
          computed_value = `member-${nestedValue?.[0]?.external_id}`;
        }
        else {
          computed_value = `custom-${nestedValue?.[0]?.name}`;
        }
        current_item[nestedKey] = computed_value;
      }

      else if (['priority', 'category', 'tags'].includes(nestedKey)) {
        current_item[nestedKey] = !nestedValue || !nestedValue.length ? null : nestedValue;
      }
      else {
        current_item[nestedKey] = convertTableUnitsData(nestedValue, null);
      }
    }

    array.push(current_item);
  }

  return array;
}

const columns_widths_map = computed(() => widget_data.value.columns_widths || {});

function generateColumns(data, response_data) {
  const column_array = [{
    accessorKey: 'name',
    id: 'name',
    header: 'Activity',
    size: columns_widths_map.value?.name || '400',
  }];
  data.forEach((item) => {
    let header_name = '';
    if (item.name === 'id')
      header_name = 'Activity id';
    else if (item.key && response_data?.[item.key]?.units)
      header_name = `${item.name} (${item.key && response_data?.[item.key]?.units})`;
    else
      header_name = item.name;

    column_array.push({
      accessorKey: item.key,
      header: header_name,
      id: item.key,
      cell: info => `${trimStart(info.getValue(), '-')} `,
      ...(item?.column_types?.length
        ? {
            columns: item.column_types.map((child) => {
              const col_id = `${child}-${item.key}`;
              return {
                accessorKey: col_id,
                id: col_id,
                header: child === 'id' ? 'Activity id' : column_key_map[child],
                size: columns_widths_map.value?.[col_id] || '150',
              };
            },
            ),
          }
        : {
            size: columns_widths_map.value?.[item.key] || '150',
          }),
    });
  },
  );
  columns.value = [];
  columns.value = [...column_array];
}

async function rowExpanded(e) {
  const payload_copy = cloneDeep(props.payload);
  payload_copy.filters = { ...payload_copy.filters, advanced_filters: [{ field: 'parent', operator: 'is', value: e.row.id }, ...(props.payload?.filters?.advanced_filters || [])] };
  payload_copy.data.activities = [];

  const { data } = await $services.project_management.get_graph({ body: payload_copy });
  const child = [];
  child.push(...generateActivities(data.data));

  if (!children.value[e.row.id] && child.length) {
    children.value[e.row.id] = child;
    forceUpdate();
    updatePrintDataset(e.row.id, child);
    updatePrintMap();
  }
}

function forceUpdate() {
  const copy = markRaw(activities.value);
  activities.value = [...copy];
}
function getChildren(row) {
  if (row?.subRows?.length) {
    if (children.value[row.id]?.length)
      return children.value[row.id];
    else
      return row?.subRows;
  }
}

function updatePrintDataset(parent_id, children) {
  if (!children?.length)
    return;

  const index = findIndex(print_dataset.value, a => a.id === parent_id);
  print_dataset.value.splice(index + 1, 0, ...children);
}

const status_color = {
  'On track': 'blue',
  'Delayed': 'red',
  'Completed': 'green',
  'Not Started': 'gray',
};

const height = computed(() => {
  return ((props.data.h || 22) * 20) - 44;
});

function formatTable(cell) {
  // this is failing when expanding rows, might be the issue for the flickering
  if (startsWith(cell.getValue(), '-'))
    return { color: '#DC2626' };
  return {};
}

function updatePrintMap() {
  dashboard_store.update_print_map(props.id, {
    type: widget_data.value.type,
    renderAt: `chart-container-${props?.id}`,
    renderType: (props.data.data.activity_table_chart || props.data.data?.chart_type !== 'none') ? 'combined_table_graph' : 'table',
    width: '100%',
    height: '100%',
    dataFormat: 'json',
    chart_name: widget_data.value.name,
    dimensions: {
      x: props.data.x,
      y: props.data.y,
    },
    dataSource: {
      columns: columns.value,
      activities: print_dataset.value,
      dataset: print_dataset.value,
      is_transpose: widget_data.value.transpose === true,
      dashboard_index: props.data.i,
      is_new_pivot_chart: widget_data.value.chart === 'workflow_pivot_table',
      ...((props.data.data.activity_table_chart || props.data.data?.chart_type !== 'none') ? { graph_config: props.graph_config } : {}),
    },
  });
  dashboard_store.update_new_print_map((props.data?.data?.name || 'untitled'), {
    widget_type: 'table',
    type: props.data?.data?.type,
    dataSource: parseNewPrintData(activities.value),
  });
}

function flattenActivities(data, childDataMap) {
  const result = [];

  function addActivity(activity) {
    result.push(activity);
    if (childDataMap[activity.id]) {
      childDataMap[activity.id].forEach(child => addActivity(child));
    }
  }

  data.forEach(activity => addActivity(activity));

  return result;
}

function parseNewPrintData(data) {
  const accessor_keys = columns.value.map(c => c.accessorKey);
  const flat_activities = flattenActivities(data, children.value);
  const parsed_data = [];
  flat_activities.forEach((d) => {
    parsed_data.push(accessor_keys.map(k => toString(d[k])));
  });
  return parsed_data;
}

function columnResized(_resized_column, columns_widths) {
  // prevents the table from rerendering
  prevent_watcher.value = true;
  dashboard_store.set_table_column_widths(
    props?.id,
    columns_widths,
  );
}

const force_update = ref(0);
watch(() => props.response_data, async () => {
  if (!prevent_watcher.value) {
    await getReports();
    if (props?.id !== 'preview')
      updatePrintMap();
    force_update.value++;
  }
}, { immediate: true, deep: true });
</script>

<template>
  <div>
    <div v-if="no_data || !activities?.length" class="text-sm font-semiBold w-full" :class="dashboard_store.is_mobile_view ? 'h-[240px] grid place-items-center' : 'mt-8 flex justify-center'">
      {{ $t('No data present') }}
    </div>
    <a v-if="activities && columns?.length && activities?.length ">
      <div class="w-full scrollbar" :style="{ height: `${content_height || height}px` }">
        <HawkTable
          :key="force_update"
          :data="activities"
          :columns="columns"
          freeze_column_id="name"
          :show_menu_header="false"
          :show_column_borders="true"
          :format-table="formatTable"
          additional_table_classes=""
          :container_class="`h-[${content_height || height}px]`"
          :striped="true"
          is_gapless
          :get-children="getChildren"
          :disable_resize="!dashboard_store.is_editing_dashboard"
          cell_height="30px"
          @sub-rows="rowExpanded($event)"
          @column-resized="columnResized"
        >
          <template #status="status">
            <HawkBadge :color="status_color[status.data.getValue()]">
              {{ status.data.getValue() }}
            </HawkBadge>
          </template>
          <template #resources="resources">
            <p v-if="resources.data.getValue() === '_'">
              -
            </p>
            <p v-else-if="resources.data.getValue().split('-')[0] === 'custom' ">
              {{ resources.data.getValue().split('-')[1] }}
            </p>
            <p v-else>
              <hawk-members :members="resources.data.getValue().split('-')[1] " type="badge" size="sm" class="mr-2" />
            </p>
          </template>
          <template #tags="tags">
            <hawk-tags-name
              v-if="tags.data.getValue()" :tags="tags.data?.getValue()"
            />
            <div v-else>
              -
            </div>
          </template>
          <template #priority="priority">
            <div v-if="priority.data.getValue()">
              {{ priority_values[priority.data.getValue()] }}
            </div>
            <div v-else>
              -
            </div>
          </template>
          <template #category="category">
            <hawk-category-name v-if="category.data.getValue()" :uid="category.data.getValue()" />
            <div v-else>
              -
            </div>
          </template>
        </HawkTable>
      </div>
    </a>
  </div>
</template>
